import { useEffect, useMemo, useState } from "react";
import { listBoStaff, type BoMember } from "../../api";
import { fill, shortDate, venueLabel, type BoProps } from "./Backoffice";

export function Staff({
  t,
  locale,
  onError,
  codeFilter,
  onClearCode,
}: BoProps & { codeFilter: string | null; onClearCode: () => void }) {
  const [staff, setStaff] = useState<BoMember[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [venue, setVenue] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    listBoStaff()
      .then((r) => {
        setStaff(r.staff);
        setLoadError(null);
      })
      .catch((e) => setLoadError(onError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const venueNames = useMemo(
    () => [...new Set(staff.map((m) => m.venue.name).filter(Boolean))].sort((a, b) => a.localeCompare(b, locale)),
    [staff, locale],
  );
  const q = query.trim().toLowerCase();
  const shown = staff.filter(
    (m) =>
      (!codeFilter || m.code === codeFilter) &&
      (!venue || m.venue.name === venue) &&
      (!q || m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)),
  );

  return (
    <div className="bo">
      <h2>{t("bo.nav.staff")}</h2>
      <div className="form-row">
        <select value={venue} onChange={(e) => setVenue(e.target.value)}>
          <option value="">{t("bo.staff.all_venues")}</option>
          {venueNames.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <input value={query} placeholder={t("bo.staff.search")} onChange={(e) => setQuery(e.target.value)} />
        {codeFilter && (
          <span className="chip">
            {fill(t("bo.staff.code_filter"), { code: codeFilter })}
            <button className="link" onClick={onClearCode} aria-label={t("common.cancel")}>
              ×
            </button>
          </span>
        )}
      </div>
      {loadError && <p className="error">{loadError}</p>}
      {!loadError && (
        <p className="muted small">{shown.length === 1 ? t("bo.staff.count_one") : fill(t("bo.staff.count"), { n: shown.length })}</p>
      )}
      {shown.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t("bo.staff.name")}</th>
                <th>{t("bo.staff.email")}</th>
                <th>{t("bo.staff.venue")}</th>
                <th>{t("bo.staff.code")}</th>
                <th>{t("bo.staff.status")}</th>
                <th>{t("bo.staff.signed_up")}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((m, i) => (
                <tr key={`${m.email}-${i}`} className={m.status === "closed" ? "dim" : ""}>
                  <td className="strong">{m.name}</td>
                  <td className="mono">{m.status === "closed" ? "—" : m.email}</td>
                  <td>{m.venue.name ? venueLabel(m.venue) : <span className="muted">—</span>}</td>
                  {/* Empty for sign-ups from before the code was recorded. */}
                  <td className="mono">{m.code || <span className="muted" title={t("bo.staff.code_unknown")}>—</span>}</td>
                  <td>
                    <span className={`badge ${m.status}`}>{t(`bo.staff.status.${m.status}`)}</span>
                  </td>
                  <td className="muted">{shortDate(m.signed_up_at, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
